// Specs — a live, mutable dependency graph (sections + dependency edges) that
// renders into a single readable document on demand. Attach one to an agent
// and its threads gain spec_* tools (show/add_section/dep_add/claim/...);
// this page creates/browses the specs themselves.
import { useClient } from '@cortex/react-query';
import type { Spec } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ConfirmDialog } from '../../../../components/common/ConfirmDialog';
import { EmptyState } from '../../../../components/common/EmptyState';
import { Modal } from '../../../../components/common/Modal';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import { routes } from '../../../../lib/constants';
import { formatRelative } from '../../../../lib/format';

export const Route = createFileRoute('/agents/orchestration/specs/')({
  component: SpecsPage,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/** List-view shape — Spec plus the fields the list endpoint adds. */
type SpecListItem = Spec & { agentIds: string[]; sectionCount: number };

/** Spec names are kebab-case (matches Skill's naming rule). Slugify free-typed input. */
function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function SpecsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<SpecListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['specs'],
    queryFn: () => client.http.get('v1/specs').json<IListEnvelope<SpecListItem>>(),
  });

  const slug = slugify(name);

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/specs', {
          json: {
            name: slug,
            ...(description.trim() ? { description: description.trim() } : {}),
          },
        })
        .json<Spec>(),
    onSuccess: async (spec) => {
      setShowCreate(false);
      setName('');
      setDescription('');
      await qc.invalidateQueries({ queryKey: ['specs'] });
      fork(navigate({ to: routes.orchestrationSpec(spec.id) }));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/specs/${id}`).text(),
    onSuccess: () => {
      setDeleteTarget(null);
      fork(qc.invalidateQueries({ queryKey: ['specs'] }));
    },
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Mission Specs"
        actions={
          <button className="btn btn--sm btn--primary" onClick={() => setShowCreate(true)}>
            New spec
          </button>
        }
      />

      {listQuery.isLoading && <Spinner />}
      {!listQuery.isLoading && items.length === 0 && (
        <EmptyState
          title="No specs yet"
          description="Create one, add a few sections (or let an agent write them), then attach it to a persistent agent."
        />
      )}

      {items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Items</th>
                <th>Bound agents</th>
                <th>Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((spec) => (
                <tr
                  key={spec.id}
                  onClick={() => navigate({ to: routes.orchestrationSpec(spec.id) })}
                  style={{ cursor: 'pointer' }}
                  title="Open spec"
                >
                  <td>
                    <div style={{ fontWeight: 600 }}>{spec.name}</div>
                    {spec.description && (
                      <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                        {spec.description}
                      </div>
                    )}
                  </td>
                  <td className="t-xs">{spec.sectionCount}</td>
                  <td className="t-xs">
                    {spec.agentIds.length === 0 ? (
                      <span className="t-muted">—</span>
                    ) : (
                      spec.agentIds.length
                    )}
                  </td>
                  <td className="t-xs t-muted">{formatRelative(spec.updatedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(spec);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => {
          if (!create.isPending) setShowCreate(false);
        }}
        title="New spec"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={create.isPending}
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={create.isPending || slug.length === 0}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Name
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ghost obstacle detection"
              autoFocus={true}
            />
            {name.trim().length > 0 && (
              <span className="t-xs t-muted">
                Saved as <code>{slug || '(empty)'}</code> — spec names are kebab-case
              </span>
            )}
          </label>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Description <span className="t-muted">(optional)</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Design spec for the ghost-obstacle detection feature"
            />
          </label>
          {create.isError && (
            <div className="alert alert--danger">
              {String((create.error as Error)?.message ?? 'Failed to create spec')}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => {
          if (!remove.isPending) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
        eyebrow={deleteTarget?.name}
        title="Delete this spec?"
        message="This permanently removes the spec. Sections, dependency edges, and versions are retained but no longer reachable — agents bound to it lose their spec_* tools for it immediately."
        confirmLabel="Delete"
        destructive={true}
        pending={remove.isPending}
      />
    </div>
  );
}
